import React, { Fragment } from 'react';
import { Prompt, withRouter } from 'react-router-dom';

import { FlexRow, onFieldChange, toasty } from '../common/forms/FormElements';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import { isEqual } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardCheck, faSave, faTimes } from '@fortawesome/free-solid-svg-icons'
import {
    AppPageForm,
    FlexCenterRow,
    FormGroupColumn,
    FormLabel,
    GroupedRow
} from '../common/forms/FormElements';

import { LinearProgress } from '@material-ui/core';
import { util } from '../Util';

import {
    Button,
    FormGroup
} from 'reactstrap';

import { handleFormSaveError } from '../common/forms/ValidationError';
import authService from '../api-authorization/AuthorizeService';
import { getUserProfile } from '../common/UserProfile';
import FormErrorResponseDisplay from '../common/forms/FormErrorResponseDisplay';
import { WorkedOnBySalesman } from './Quote';


class WorkedOnByForm extends React.Component {

    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            formOpened: false,
            formValidated: false,
            loading: true,
            saving: false,
            statuses: [],
            perms: [],
            errorResponse: {}
        }
        this.onSubmit = this.onSubmit.bind(this);
        this.save = this.save.bind(this);
        this.saveNew = this.saveNew.bind(this);
        this.onChange = this.onChange.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount = () => {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount = async () => {
        await authService.unsubscribe(this._subscription);
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (!!isAuthenticated) {
            let { id } = { ...this.props.match.params };
            const { userPermissions } = await getUserProfile();

            var [workedOnBySalesmanResponse] = await Promise.all([
                !!id ? util.fetch.get(ApiRoutes.quote.quoteWorkedOnByById(id), util.fetch.format.none) : new WorkedOnBySalesman()
            ]);

            var workedOnBySalesman;

            //Handle any issues fetching data
            if (!!id && !workedOnBySalesmanResponse.ok) {
                //Handle erroneous links entered by the user
                if (workedOnBySalesmanResponse.status === 404)
                    this.props.history.push(AppNavPaths.NotFound);
                else
                    this.props.history.push(AppNavPaths.ServerError);
                return false;
            } else {
                workedOnBySalesman = !!id ? await workedOnBySalesmanResponse.json() : workedOnBySalesmanResponse;
            }

            this.setState({
                formValidated: false,
                saving: false,
                workedOnBySalesman: workedOnBySalesman,
                loading: false,
                originalData: workedOnBySalesman,
                perms: userPermissions,
            });
        }
    }

    //#region METHODS
    onChange = onFieldChange;

    onSubmit() {
        let { workedOnBySalesman } = { ...this.state };
        this.clearSaving()
        this.setState({ errors: {}, saving: true });

        if (this.props.location.pathname === AppNavPaths.QuoteWorkedOnByNew)
            this.saveNew(workedOnBySalesman);
        else
            this.save(workedOnBySalesman);
    }

    clearSaving = () => this.setState({ saving: false });

    notifySuccess = () => toasty.success('`Quote Worked on By` Saved', '`Quote Worked on By` saved successfully.');
    notifyError = (message) => toasty.error('Save Unsuccessful', message);
    handleSaveError = (err) => handleFormSaveError(this, err);

    displaySaveErrors = (response) => this.setState({ errorResponse: response });
    clearSaveErrors = () => this.setState({ errorResponse: {} });

    onCloseClicked = () => {
        this.props.history.push(`${AppNavPaths.QuotesWorkedOnBy}`);
    }

    saveNew = async (workedOnBySalesman) => {
        try {
            let response = await util.fetch.post(ApiRoutes.quote.quoteWorkedOnByCreate(), workedOnBySalesman, util.fetch.format.none);
            if (response.redirected) {
                window.location.href = response.url;
            } else if (!!response.ok) {
                var serviceResponse = await response.json();
                if (serviceResponse.result === 'SUCCESS') {
                    this.notifySuccess();
                    let workedOnBySalesmanId = serviceResponse.data;
                    this.props.history.push(`${AppNavPaths.QuoteWorkedOnBy}/${workedOnBySalesmanId}`);
                } else {
                    this.notifyError(serviceResponse.message);
                }
            } else {

                if (response.status === 400) {
                    let serviceResponse = await response.json();
                    this.displaySaveErrors(serviceResponse);
                } else {

                    let errorResp = await response.json();

                    if (errorResp.errors) {
                        this.displaySaveErrors(errorResp);
                    } else {
                        let serviceResponse = {
                            title: 'Server Error',
                            errors: {
                                Exception: [
                                    await response.text()
                                ]
                            }
                        }
                        this.displaySaveErrors(serviceResponse);
                    }
                }
            }
        } catch (error) {
            this.notifyError(error.toString());
        } finally {
            this.clearSaving();
        }
    }

    save = async (workedOnBySalesman) => {
        try {
            let response = await util.fetch.put(ApiRoutes.quote.quoteWorkedOnByById(workedOnBySalesman.id), workedOnBySalesman, util.fetch.format.none);
            if (response.redirected) {
                window.location.href = response.url;
            } else if (!!response.ok) {
                this.notifySuccess();
                this.populateState();
            } else {
                this.displaySaveErrors(await response.json());
            }
        } catch (error) {
            this.notifyError(error.toString());
        } finally {
            this.clearSaving();
        }
    }

    //#endregion

    //#region RENDERING
    render() {
        const { loading, originalData, workedOnBySalesman, perms } = { ...this.state };
        let { errorResponse } = { ...this.state };

        if (!!loading) {
            return (<LinearProgress variant="indeterminate" color="secondary" />);
        } else {
            const isNewWorkedOnBySalesman = ((workedOnBySalesman.id ?? 0) <= 0);
            const saveWorkedOnBySalesmanButtonText = (!isNewWorkedOnBySalesman ? 'Save' : 'Save New `Quote Worked on By`');
            const canEdit = !!perms.includes("quote_worked_on_by.edit") || (isNewWorkedOnBySalesman && !!perms.includes("quote_worked_on_by.create"));

            return (
                <Fragment>
                    <AppPageForm
                        formId={"workedOnByForm"}
                        formHeadingIcon={faClipboardCheck}
                        formHeading={isNewWorkedOnBySalesman ? 'New `Quote Worked on By`' : 'Edit `Quote Worked on By`'}
                        formName={"workedOnByForm"}
                        formRef={this.formRef}
                        onSubmit={this.onSubmit}
                        setIsValidated={(value) => { this.setState({ formValidated: value }) }}
                        isValidated={this.state.formValidated}
                        saving={this.state.saving}
                        errors={this.state.errors}
                        loading={this.state.loading}
                    >
                        <GroupedRow>
                            <FormGroupColumn>
                                <FormGroup>
                                    <FormLabel htmlFor="inputName" text="Name" required={true} />
                                    <input id="name"
                                        name="workedOnBySalesman.name"
                                        autoComplete="off"
                                        className="form-control"
                                        required
                                        disabled={!canEdit}
                                        onChange={!!canEdit? this.onChange : undefined}
                                        defaultValue={workedOnBySalesman.name} />
                                    <small className="invalid-feedback text-danger" hidden>Name is required.</small>
                                </FormGroup>
                            </FormGroupColumn>
                        </GroupedRow>
                        <FlexCenterRow className="mb-3">
                            {!!canEdit &&
                                <Button
                                    size="sm"
                                    type="submit"
                                    disabled={this.state.saving}
                                    color="primary"
                                    name="workedOnByForm">
                                    <FontAwesomeIcon
                                        className="mr-2"
                                        icon={faSave} />
                                    {saveWorkedOnBySalesmanButtonText}
                                </Button>
                            }
                            <Button
                                size="sm"
                                type="button"
                                color="secondary"
                                disabled={this.state.saving}
                                name="workedOnByFormClose"
                                onClick={this.onCloseClicked}
                                className="ml-2">
                                <FontAwesomeIcon
                                    className="mr-2"
                                    icon={faTimes} />
                                {"Close"}
                            </Button>
                        </FlexCenterRow>
                        <FlexRow>
                            <FormErrorResponseDisplay onClear={this.clearSaveErrors} response={errorResponse} />
                        </FlexRow>
                    </AppPageForm>
                    <Prompt
                        when={!this.state.saving && !isEqual(originalData, workedOnBySalesman)}
                        message='You have unsaved changes, are you sure you want to leave?'
                    />
                </Fragment>
            );
        }

    }
    //#endregion
}

export default withRouter(WorkedOnByForm);